import jsPDF from 'jspdf';

type PdfOptions = {
  filename?: string;
  marginPt?: number;
};

function textOf(el: Element | null | undefined): string {
  const t = el?.textContent ?? '';
  return t.replace(/\s+/g, ' ').trim();
}

function linesFromResumenVisual(root: HTMLElement): string[] {
  const lines: string[] = [];
  const title = textOf(root.querySelector('.resumen_visual__titulo')) || 'Resumen';
  lines.push(title.toUpperCase(), '');

  const blocks = Array.from(root.querySelectorAll('.resumen_visual__bloque'));
  blocks.forEach((b) => {
    const sub = textOf(b.querySelector('.resumen_visual__subtema'));
    if (sub) lines.push(sub, '');
    const puntos = Array.from(b.querySelectorAll('.resumen_visual__punto'))
      .map((p) => textOf(p))
      .filter(Boolean);
    puntos.forEach((p) => lines.push(`- ${p}`));
    lines.push('');
  });

  const idea = textOf(root.querySelector('.resumen_visual__idea'));
  const txt = textOf(root.querySelector('.resumen_visual__texto'));
  if (idea || txt) {
    lines.push((idea || 'Idea clave').toUpperCase());
    if (txt) lines.push(txt);
  }

  return lines;
}

function linesFromResumenExtenso(root: HTMLElement): string[] {
  const lines: string[] = [];
  const title = textOf(root.querySelector('.resumen-extenso__titulo')) || 'Resumen';
  lines.push(title.toUpperCase(), '');

  const sections = Array.from(root.querySelectorAll('.resumen-extenso__seccion'));
  sections.forEach((sec) => {
    const header = textOf(sec.querySelector('.resumen-extenso__encabezado'));
    if (header) lines.push(header.toUpperCase());

    // contenido directo de sección (solo los que NO estén dentro de subapartado)
    const contents = Array.from(sec.querySelectorAll('.resumen-extenso__contenido'))
      .filter((c) => !c.closest('.resumen-extenso__subapartado'))
      .map((c) => textOf(c))
      .filter(Boolean);
    contents.forEach((t) => lines.push(t));

    // subapartados
    const subapartados = Array.from(sec.querySelectorAll('.resumen-extenso__subapartado'));
    subapartados.forEach((sub) => {
      const subt = textOf(sub.querySelector('.resumen-extenso__subtitulo'));
      if (subt) lines.push('', subt);
      const subText = textOf(sub.querySelector('.resumen-extenso__contenido'));
      if (subText) lines.push(subText);
    });

    // puntos clave (si existen)
    const puntos = Array.from(sec.querySelectorAll('.resumen-extenso__punto'))
      .map((p) => textOf(p))
      .filter(Boolean);
    if (puntos.length) {
      lines.push('');
      puntos.forEach((p) => lines.push(`- ${p}`));
    }

    lines.push('');
  });

  return lines;
}

function linesFromElement(element: HTMLElement): string[] {
  const rv = element.matches('.resumen_visual')
    ? element
    : (element.querySelector('.resumen_visual') as HTMLElement | null);
  if (rv) return linesFromResumenVisual(rv);

  const re = element.matches('.resumen-extenso')
    ? element
    : (element.querySelector('.resumen-extenso') as HTMLElement | null);
  if (re) return linesFromResumenExtenso(re);

  // Fallback genérico: texto plano del elemento
  const fallback = (element.innerText || element.textContent || '').trim();
  return fallback ? fallback.split('\n').map((l) => l.trim()).filter(Boolean) : ['Resumen'];
}

function writeLinesToPdf(
  pdf: jsPDF,
  lines: string[],
  { marginPt = 36 }: { marginPt?: number } = {}
) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const maxWidth = pageWidth - marginPt * 2;
  const top = marginPt;
  const bottom = pageHeight - marginPt;

  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);

  let y = top;
  const lineHeight = 14;

  const newPageIfNeeded = () => {
    if (y > bottom) {
      pdf.addPage();
      y = top;
    }
  };

  for (const rawLine of lines) {
    const line = rawLine ?? '';

    // títulos “tipo apartado”
    const isHeading = line === line.toUpperCase() && line.length >= 3 && !line.startsWith('- ');
    if (isHeading) {
      newPageIfNeeded();
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      const wrapped = pdf.splitTextToSize(line, maxWidth);
      for (const w of wrapped) {
        pdf.text(w, marginPt, y);
        y += lineHeight + 2;
        newPageIfNeeded();
      }
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      continue;
    }

    if (!line.trim()) {
      y += 8;
      newPageIfNeeded();
      continue;
    }

    const wrapped = pdf.splitTextToSize(line, maxWidth);
    for (const w of wrapped) {
      pdf.text(w, marginPt, y);
      y += lineHeight;
      newPageIfNeeded();
    }
  }
}

/**
 * Exporta un resumen a PDF como TEXTO PLANO (sin CSS) y dispara la descarga.
 * - Funciona sin backend.
 * - Distingue apartados para `resumen_visual` y `resumen-extenso`.
 */
export async function downloadElementAsPdf(
  element: HTMLElement,
  { filename = 'resumen.pdf', marginPt = 36 }: PdfOptions = {}
) {
  const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
  const lines = linesFromElement(element);
  writeLinesToPdf(pdf, lines, { marginPt });
  pdf.save(filename);
}


